/**
 * Cerner open FHIR service — no auth required.
 *
 * Tries the Cerner Millennium sandbox open endpoint.
 * Returns null on any failure so callers can fall back to dummy data.
 *
 * Open endpoint: https://fhir.cerner.com/{tenant}/open/r4/
 * Docs: https://fhir.cerner.com/millennium/r4/
 */

const OPEN_BASE = 'https://fhir.cerner.com/ec2458f2-1e24-41c8-b71b-0e701af7583d/open/r4';
// Known Cerner millennium sandbox patient with rich demo data
const DEMO_PATIENT = '12724066';
const TIMEOUT_MS = 5000;

async function openGet(path: string): Promise<unknown | null> {
  try {
    const res = await fetch(`${OPEN_BASE}${path}`, {
      headers: { Accept: 'application/fhir+json' },
      signal: AbortSignal.timeout(TIMEOUT_MS),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

function entries(bundle: unknown): unknown[] {
  return (bundle as { entry?: { resource: unknown }[] })?.entry?.map((e) => e.resource) ?? [];
}

// ─── Conditions ────────────────────────────────────────────────────────────

export interface FhirCondition {
  icd: string;
  display: string;
  status: string;
  onset: string;
}

export async function fetchFhirConditions(): Promise<FhirCondition[] | null> {
  const bundle = await openGet(`/Condition?patient=${DEMO_PATIENT}&clinical-status=active&_count=20`);
  if (!bundle) return null;
  const items = entries(bundle);
  if (!items.length) return null;
  return items.map((r: unknown) => {
    const res = r as Record<string, unknown>;
    const coding = ((res.code as Record<string, unknown>)?.coding as unknown[])?.[0] as Record<string, string> ?? {};
    return {
      icd: coding.code ?? '',
      display: coding.display ?? (res.code as Record<string, string>)?.text ?? 'Unknown condition',
      status: ((res.clinicalStatus as Record<string, unknown>)?.coding as unknown[])?.[0] as Record<string, string> === undefined ? 'active' : (((res.clinicalStatus as Record<string, unknown>)?.coding as unknown[])?.[0] as Record<string, string>)?.code ?? 'active',
      onset: (res.onsetDateTime as string ?? (res.onsetPeriod as Record<string, string>)?.start ?? '').split('T')[0],
    };
  });
}

// ─── Vitals ────────────────────────────────────────────────────────────────

export interface FhirVital {
  loinc: string;
  display: string;
  value: string;
  unit: string;
  date: string;
}

export async function fetchFhirVitals(): Promise<FhirVital[] | null> {
  const bundle = await openGet(
    `/Observation?patient=${DEMO_PATIENT}&category=vital-signs&_count=30&_sort=-date`,
  );
  if (!bundle) return null;
  const items = entries(bundle);
  if (!items.length) return null;
  return items.map((r: unknown) => {
    const res = r as Record<string, unknown>;
    const coding = ((res.code as Record<string, unknown>)?.coding as unknown[])?.[0] as Record<string, string> ?? {};
    const vq = res.valueQuantity as Record<string, unknown> | undefined;
    return {
      loinc: coding.code ?? '',
      display: coding.display ?? (res.code as Record<string, string>)?.text ?? 'Unknown',
      value: vq ? String(vq.value) : ((res.valueCodeableConcept as Record<string, string>)?.text ?? String(res.valueString ?? '—')),
      unit: (vq?.unit as string) ?? '',
      date: ((res.effectiveDateTime as string) ?? (res.effectivePeriod as Record<string, string>)?.start ?? '').split('T')[0],
    };
  }).filter((v) => v.value !== '—');
}

// ─── Medications ───────────────────────────────────────────────────────────

export interface FhirMedication {
  name: string;
  sig: string;
  status: string;
  date: string;
}

export async function fetchFhirMedications(): Promise<FhirMedication[] | null> {
  const bundle = await openGet(
    `/MedicationRequest?patient=${DEMO_PATIENT}&status=active&_count=20`,
  );
  if (!bundle) return null;
  const items = entries(bundle);
  if (!items.length) return null;
  return items.map((r: unknown) => {
    const res = r as Record<string, unknown>;
    const name =
      (res.medicationCodeableConcept as Record<string, string>)?.text ??
      (res.medicationReference as Record<string, string>)?.display ??
      'Unknown medication';
    const dosage = (res.dosageInstruction as Record<string, string>[])?.[0];
    return {
      name,
      sig: dosage?.text ?? dosage?.patientInstruction ?? '',
      status: (res.status as string) ?? 'active',
      date: ((res.authoredOn as string) ?? '').split('T')[0],
    };
  });
}

// ─── Convenience: fetch all at once ───────────────────────────────────────

export interface FhirData {
  conditions: FhirCondition[] | null;
  vitals: FhirVital[] | null;
  medications: FhirMedication[] | null;
  source: 'fhir' | 'fallback';
}

export async function fetchAllFhirData(): Promise<FhirData> {
  const [conditions, vitals, medications] = await Promise.all([
    fetchFhirConditions(),
    fetchFhirVitals(),
    fetchFhirMedications(),
  ]);
  const hasData = Boolean(conditions?.length || vitals?.length || medications?.length);
  return { conditions, vitals, medications, source: hasData ? 'fhir' : 'fallback' };
}
